import React, { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { ArrowDownIcon, CheckIcon, ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import MessageBox from "./MessageBox";
import { CancelAppointments, fetchAppointmentWithTenant, RejectConfirmAppointment } from "./DataServices/AppointmentsDataServices";
import { useParams } from "react-router-dom";
import { formatDateTime } from "./VacantConfirmation";
import Loading from "./Loading";

interface IProspectiveTenants {
  open: boolean;
  setOpen: (open: boolean) => void;
  groupId: string;
  setShowComposeEmail: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedTenantsToEmail: React.Dispatch<React.SetStateAction<Set<{ email: string, dept: string }>>>;
  Appartment: string;
  openNotification: boolean;
  setOpenNotification: React.Dispatch<React.SetStateAction<boolean>>;
}
interface IProspectiveTenant {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  notes: string;
}

interface AppointmentWithTenant {
  id: string;
  startTime: Date;
  endTime: Date;
  tenants: IProspectiveTenant[]
}

export default function ProspectiveTenants({
  open,
  setOpen,
  groupId,
  setShowComposeEmail,
  Appartment,
  setSelectedTenantsToEmail,
  setOpenNotification,
}: IProspectiveTenants) {
  const [openMessageBox, setOpenMessageBox] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedProspectiveTenants, setSelectedProspectiveTenants] = useState<string[]>([]);
  const [selectedArhId, setSelectedArhId] = useState<string[]>([]);
  const [selectedAppointment, setSelectedAppointment] = useState<string[]>([])
  const [appointmentTenantData, setAppointmentTenantData] = useState<AppointmentWithTenant[]>([])
  const [checked, setChecked] = useState<boolean>(false);
  const [cancelMainAppt, setCancelMainAppt] = useState(false)
  const [expandedRows, setExpandedRows] = useState<string[]>([]);

  const toggleRow = (id: string) => {
    setExpandedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  };
  useEffect(() => {
    open && fetchAppointmentWithTenant(groupId)
      .then(data => {
        setAppointmentTenantData(data);
      })
  }, [groupId, open])

  const toggleAll = () => {
    if (!checked) {
      appointmentTenantData.forEach((appt) => {
        setSelectedAppointment((prev) => [...prev, appt.id]);
      })
    }
    else
      setSelectedAppointment([]);

    setChecked(!checked);
  }
  const handleProspectiveTenantSelection = (tenants: IProspectiveTenant[]) => {
    setSelectedProspectiveTenants((prevSelected) => {
      let updated = [...prevSelected];

      tenants.forEach(tenant => {
        if (updated.includes(tenant.email)) {
          // Remove if already selected
          updated = updated.filter(email => email !== tenant.email);
        } else {
          // Add if not selected
          updated.push(tenant.email);
        }
      });
      return updated;
    });
    setSelectedArhId((prevSelected) => {
      let updated = [...prevSelected];

      tenants.forEach(tenant => {
        if (updated.includes(tenant.id)) {
          // Remove if already selected
          updated = updated.filter(id => id !== tenant.id);
        } else {
          // Add if not selected
          updated.push(tenant.id);
        }
      });
      return updated;
    });

  };

  const isTenantsSelected = (tenants: IProspectiveTenant[]) => {
    return tenants.every(tenant => selectedProspectiveTenants.includes(tenant.email));
  }

  const handleAppointSelection = (apptId: string) => {
    setSelectedAppointment((prevSelected) => {
      let newSelected;
      if (prevSelected.includes(apptId)) {
        newSelected = prevSelected.filter((id) => apptId !== id);
      } else {
        newSelected = [...prevSelected, apptId];
      }
      return newSelected;
    });
  }

  const handleCancelAppointments = async () => {
    try {
      setOpenMessageBox(false);
      setLoading(true);
      if (selectedAppointment.length > 0 || cancelMainAppt) await CancelAppointments(cancelMainAppt ? [groupId] : selectedAppointment, cancelMainAppt);
      else if (selectedArhId.length > 0) await RejectConfirmAppointment(selectedArhId, false);
      setOpenNotification(true);
      setTimeout(() => {
        setOpenNotification(false);
      }, 3000);
      setOpenMessageBox(false);
      setLoading(false);
      setCancelMainAppt(false);
      setSelectedAppointment([]);
      setSelectedArhId([])
      setOpen(false);
    } catch (error) {
      setLoading(false)
      console.error("Error cancelling appointments:", error);
    }
  };

  const handleSendEmail = () => {
    setShowComposeEmail(true);
    const newSelectedTenants = new Set<{ email: string, dept: string }>;
    selectedProspectiveTenants.forEach((item) => {
      newSelectedTenants.add({ email: item, dept: Appartment });
    })
    setChecked(false);
    setSelectedTenantsToEmail(newSelectedTenants);
    setOpen(false);
    setSelectedProspectiveTenants([]);
  }

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={setOpen}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-15 w-screen overflow-y-auto">
          <div className="flex min-h-full min-w-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 w-max h-full max-h-[80vh] max-w-xl sm:p-6 overflow-y-auto">
                <div>
                  <div className="text-center">
                    <div className="flex items-center">
                      <Dialog.Title
                        as="h3"
                        className="flex-1 text-base font-semibold leading-6 text-gray-900"
                      >
                        Confirmed Appointments
                      </Dialog.Title>
                      <button
                        type="button"
                        className="rounded-md border-2 border-black px-2 py-1 text-sm font-semibold text-black shadow-sm "
                        onClick={() => {
                          setOpen(false);
                          setChecked(false);
                          setSelectedProspectiveTenants([]);
                          setAppointmentTenantData([]);
                          setSelectedAppointment([]);
                        }}
                      >
                        X
                      </button>
                    </div>

                    <div className="mt-2">
                      <div className="px-2">
                        <div className="sm:flex sm:items-center">
                          <div className="sm:flex-auto">
                            <h1 className="text-base font-semibold leading-6 text-gray-900">
                              Users
                            </h1>
                            <p className="mt-2 text-justify text-sm text-gray-700">
                              A list of all the prospective tenants who have
                              confirmed their attendance for the showing
                              including their name, and email.
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-start my-4 w-full gap-3">
                          <input type="checkbox" checked={cancelMainAppt} onChange={() => setCancelMainAppt(!cancelMainAppt)} className="rounded-sm" />
                          <span className="text-sm">Cancel Main Appointment</span>
                        </div>
                        <div className="my-6 border rounded overflow-hidden shadow bg-white">
                          <table className="w-full divide-y divide-gray-300">
                            <thead>
                              <tr className="bg-gray-100 text-left">
                                <th className="p-3"><input checked={selectedAppointment.length === appointmentTenantData.length} onChange={() => toggleAll()} className="rounded-sm" type="checkbox" /></th>
                                <th className="p-3 font-medium">Appointment</th>
                                <th className="p-3"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {appointmentTenantData.map((appt) => (
                                <React.Fragment key={appt.id}>
                                  <tr className="border-b hover:bg-gray-50">
                                    <td className="p-3">
                                      <input type="checkbox" checked={selectedAppointment.includes(appt.id)} onChange={() => handleAppointSelection(appt.id)} className="rounded-sm" />
                                    </td>
                                    <td className="p-3">{formatDateTime(appt.startTime.toString()) + " to " + formatDateTime(appt.endTime.toString())}</td>
                                    <td className="p-3 text-right">
                                      <button onClick={() => toggleRow(appt.id)} className="text-gray-600 hover:text-black">
                                        {expandedRows.includes(appt.id) ? (
                                          <ChevronUpIcon className="w-5 h-5 inline" />
                                        ) : (
                                          <ChevronDownIcon className="w-5 h-5 inline" />
                                        )}
                                      </button>
                                    </td>
                                  </tr>

                                  {expandedRows.includes(appt.id) && (
                                    <tr className="bg-gray-100 shadow-inner">
                                      <td colSpan={3} className="p-1 ">
                                        <div className="border rounded-md bg-gray-50 overflow-hidden">
                                          <table className="w-full divide-y divide-gray-300">
                                            <thead>
                                              <tr className="bg-gray-100 text-left text-sm">
                                                <th className="p-2"><input checked={isTenantsSelected(appt.tenants)} onChange={() => { handleProspectiveTenantSelection(appt.tenants); }} className="rounded-sm" type="checkbox" /></th>
                                                <th className="p-2 font-medium">Name</th>
                                                <th className="p-2 font-medium">Email</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {appt.tenants
                                                .map((tenant) => (
                                                  <tr key={tenant.id} className="border-t hover:bg-gray-100 text-left text-sm text-gray-600">
                                                    <td className="p-2">
                                                      <input checked={Boolean(isTenantsSelected([tenant]))} onChange={() => handleProspectiveTenantSelection([tenant])} className="rounded-sm" type="checkbox" />
                                                    </td>
                                                    <td className="p-2">{tenant.firstName} {tenant.lastName}</td>
                                                    <td className="p-2">{tenant.email}</td>
                                                  </tr>
                                                ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              ))}
                            </tbody>
                          </table>
                        </div>

                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-center gap-2 mt-5 sm:mt-6">
                  <button
                    type="button"
                    className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
                    onClick={() => setOpenMessageBox(true)}
                  >
                    Cancel appointments
                  </button>
                  <button
                    type="button"
                    className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
                    onClick={handleSendEmail}
                  >
                    Send Email
                  </button>
                </div>
                {loading && <div className="absolute bg-gray-300/80 top-0 left-0 w-full h-full flex items-center justify-center">
                  <Loading />
                </div>}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
        <MessageBox
          title="Appointment - Cancellation"
          message="Are you sure you want to cancel the confirmed appointments?"
          isOpen={openMessageBox}
          setIsOpen={setOpenMessageBox}
          okButtonText="Ok"
          cancelButtonText="Cancel"
          okButtonFunction={handleCancelAppointments}
          cancelButtonFunction={() => setOpenMessageBox(false)}
          okButtonStyle={{
            backgroundColor: "rgb(79, 70, 229)",
          }}
          iconColor={{ color: "rgb(79, 70, 229)" }}
        />
      </Dialog>
    </Transition.Root>
  );
}
